import React, { useState, useEffect, useCallback } from 'react';
import { 
  CheckCircle, 
  XCircle, 
  Clock, 
  User, 
  TrendingUp, 
  Calendar,
  RefreshCw,
  Search,
  History
} from 'lucide-react';
import rankRequestService from '../../services/rankRequestService';
import { toast } from 'react-toastify';
import { getRankLabel, getRankStyle } from '../../utils/rankStyles';
import './RankRequestsTable.scss';
import '../Admin/TheOilsBabe.css';

const isPendingRequest = (request) => !request.status || request.status === 'pending';

const RankRequestsTable = () => {
  const [rankRequests, setRankRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState({});
  const [selectedRequestIds, setSelectedRequestIds] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const loadRankRequests = useCallback(async () => {
    try {
      setLoading(true);
      
      // Get all rank requests from the new API
      const requests = await rankRequestService.getAllRequests();
      setRankRequests(Array.isArray(requests) ? requests : []);
      setSelectedRequestIds(new Set());
    } catch (error) {
      console.error('Error loading rank requests:', error);
      toast.error('Eroare la încărcarea cererilor de promovare');
      setRankRequests([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRankRequests();
  }, [loadRankRequests]);

  const getAvatarUrl = (request) => (
    request.avatarUrl ||
    request.profileImageUrl ||
    request.profilePictureUrl ||
    request.photoURL ||
    request.avatar ||
    request.picture ||
    ''
  );

  const handleApproveRank = async (request) => {
    try {
      setProcessing(prev => ({ ...prev, [request.id]: 'approving' }));

      // Update rank request status to approved
      await rankRequestService.updateRequestStatus(request.id, 'approved', `Approved by admin on ${new Date().toLocaleDateString()}`);

      toast.success(`Cererea de promovare pentru ${request.fullName} la ${request.requestedRank} a fost aprobată!`);
      
      // Reload requests
      await loadRankRequests();
    } catch (error) {
      console.error('Error approving rank request:', error);
      toast.error('Eroare la aprobarea cererii de promovare');
    } finally {
      setProcessing(prev => ({ ...prev, [request.id]: null }));
    }
  };

  const handleRejectRank = async (request, reason = '') => {
    try {
      setProcessing(prev => ({ ...prev, [request.id]: 'rejecting' }));

      // Update rank request status to rejected
      await rankRequestService.updateRequestStatus(request.id, 'rejected', reason || `Rejected by admin on ${new Date().toLocaleDateString()}`);

      toast.success(`Cererea de promovare pentru ${request.fullName} a fost respinsă.`);
      
      // Reload requests
      await loadRankRequests();
    } catch (error) {
      console.error('Error rejecting rank request:', error);
      toast.error('Eroare la respingerea cererii de promovare');
    } finally {
      setProcessing(prev => ({ ...prev, [request.id]: null }));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const parsedDate = typeof dateString?.toDate === 'function'
      ? dateString.toDate()
      : dateString?._seconds
        ? new Date(dateString._seconds * 1000)
        : new Date(dateString);
    if (Number.isNaN(parsedDate.getTime())) return '—';
    return parsedDate.toLocaleDateString('ro-RO', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const pendingRequests = rankRequests.filter(isPendingRequest);
  const historyRequests = rankRequests.filter((request) => request.status === 'approved');
  const requestsForActiveView = showHistory ? historyRequests : pendingRequests;
  const normalizedSearch = searchTerm.trim().toLocaleLowerCase('ro-RO');
  const visibleRequests = requestsForActiveView.filter((request) => {
    if (!normalizedSearch) return true;
    return [request.fullName, request.userEmail, request.currentRank, request.requestedRank]
      .filter(Boolean)
      .some((value) => String(value).toLocaleLowerCase('ro-RO').includes(normalizedSearch));
  });
  const visiblePendingRequests = showHistory ? [] : visibleRequests;
  const allVisibleSelected = visiblePendingRequests.length > 0 && visiblePendingRequests.every(
    (request) => selectedRequestIds.has(request.id)
  );

  const toggleRequestSelection = (requestId) => {
    setSelectedRequestIds((current) => {
      const next = new Set(current);
      if (next.has(requestId)) next.delete(requestId);
      else next.add(requestId);
      return next;
    });
  };

  const toggleSelectAllVisible = () => {
    setSelectedRequestIds((current) => {
      const next = new Set(current);
      visiblePendingRequests.forEach((request) => {
        if (allVisibleSelected) next.delete(request.id);
        else next.add(request.id);
      });
      return next;
    });
  };

  const handleBulkApprove = async (requests, label) => {
    const requestsToApprove = requests.filter(isPendingRequest);
    if (requestsToApprove.length === 0) {
      toast.info('Nu există cereri în așteptare pentru această acțiune.');
      return;
    }
    if (!window.confirm(`Aprobi ${requestsToApprove.length} ${requestsToApprove.length === 1 ? 'cerere' : 'cereri'} ${label}?`)) return;

    setBulkProcessing(true);
    setProcessing((current) => ({
      ...current,
      ...Object.fromEntries(requestsToApprove.map((request) => [request.id, 'approving'])),
    }));
    try {
      const results = await Promise.allSettled(
        requestsToApprove.map((request) => rankRequestService.updateRequestStatus(
          request.id,
          'approved',
          `Approved in bulk by admin on ${new Date().toLocaleDateString()}`
        ))
      );
      const approvedCount = results.filter((result) => result.status === 'fulfilled').length;
      const failedCount = results.length - approvedCount;
      if (approvedCount) toast.success(`${approvedCount} ${approvedCount === 1 ? 'cerere aprobată' : 'cereri aprobate'}.`);
      if (failedCount) toast.error(`${failedCount} ${failedCount === 1 ? 'cerere nu a putut fi aprobată' : 'cereri nu au putut fi aprobate'}.`);
      await loadRankRequests();
    } catch (error) {
      console.error('Error reloading rank requests after bulk approval:', error);
      toast.error('Cererile au fost procesate, dar lista nu a putut fi reîmprospătată.');
    } finally {
      setProcessing({});
      setBulkProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="rank-requests-table">
        <div className="table-header bg-oils-cream">
          <h2 className="text-oils-primary">Cereri de Promovare</h2>
        </div>
        <div className="loading-state">
          <RefreshCw className="loading-icon spinner-oils" />
          <p className="text-oils-secondary">Se încarcă cererile de promovare...</p>
        </div>
      </div>
    );
  }

  if (rankRequests.length === 0) {
    return (
      <div className="rank-requests-table">
        <div className="table-header bg-oils-cream">
          <h2 className="text-oils-primary">Cereri de Promovare</h2>
          <button onClick={loadRankRequests} className="refresh-button btn-primary-oils">
            <RefreshCw size={16} />
            Reîmprospătează
          </button>
        </div>
        <div className="empty-state card-oils">
          <CheckCircle className="empty-icon icon-oils" />
          <h3 className="text-oils-primary">Nu există cereri de promovare</h3>
          <p className="text-oils-secondary">Nu există cereri de promovare în așteptare în acest moment.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="rank-requests-table bg-oils-cream">
      <div className="table-header">
        <div>
          <span className="rank-requests-eyebrow">{showHistory ? 'ARHIVĂ' : 'DE APROBAT'}</span>
          <h2 className="text-oils-primary">{showHistory ? 'Istoric Cereri Rank' : 'Cereri de Promovare'}</h2>
        </div>
        <div className="header-actions">
          <span className="requests-count badge-pending-oils">
            {showHistory
              ? `${historyRequests.length} ${historyRequests.length === 1 ? 'cerere procesată' : 'cereri procesate'}`
              : `${pendingRequests.length} ${pendingRequests.length === 1 ? 'cerere' : 'cereri'} în așteptare`}
          </span>
          <button
            type="button"
            className={`history-button ${showHistory ? 'active' : ''}`}
            onClick={() => {
              setShowHistory((current) => !current);
              setSelectedRequestIds(new Set());
            }}
          >
            <History size={16} />
            {showHistory ? 'Cereri noi' : 'Istoric'}
          </button>
          <button onClick={loadRankRequests} className="refresh-button btn-primary-oils">
            <RefreshCw size={16} />
            Reîmprospătează
          </button>
        </div>
      </div>

      <div className="rank-requests-toolbar">
        <label className="rank-requests-search">
          <Search size={17} aria-hidden="true" />
          <input
            type="search"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Caută după nume, email sau rank"
            aria-label="Caută cereri de promovare"
          />
        </label>
        {!showHistory && <div className="rank-requests-bulk-actions">
          <button
            type="button"
            onClick={() => handleBulkApprove(
              rankRequests.filter((request) => selectedRequestIds.has(request.id)),
              'selectate'
            )}
            disabled={selectedRequestIds.size === 0 || bulkProcessing}
          >
            <CheckCircle size={16} />
            Aprobă selecția ({selectedRequestIds.size})
          </button>
          <button
            type="button"
            onClick={() => handleBulkApprove(pendingRequests, 'în așteptare')}
            disabled={pendingRequests.length === 0 || bulkProcessing}
          >
            <CheckCircle size={16} />
            Aprobă tot ({pendingRequests.length})
          </button>
        </div>}
      </div>

      <div className="table-container">
        <table className="requests-table table-oils">
          <thead>
            <tr>
              <th className="selection-cell">
                {!showHistory && <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={toggleSelectAllVisible}
                  disabled={visiblePendingRequests.length === 0}
                  aria-label="Selectează toate cererile afișate"
                />}
              </th>
              <th>Utilizator</th>
              <th>Rank Curent</th>
              <th>Rank Solicitat</th>
              <th>{showHistory ? 'Data procesării' : 'Data Cererii'}</th>
              <th>Status</th>
              <th>Acțiuni</th>
            </tr>
          </thead>
          <tbody>
            {visibleRequests.map((request) => (
              <tr key={request.id} className="request-row rank-request-card">
                <td className="selection-cell">
                  {!showHistory && <input
                    type="checkbox"
                    checked={selectedRequestIds.has(request.id)}
                    onChange={() => toggleRequestSelection(request.id)}
                    disabled={!isPendingRequest(request) || bulkProcessing}
                    aria-label={`Selectează cererea pentru ${request.fullName}`}
                  />}
                </td>
                <td className="user-cell">
                  <div className="user-info">
                    <div className="user-avatar bg-oils-primary">
                      {getAvatarUrl(request) ? (
                        <img src={getAvatarUrl(request)} alt="" />
                      ) : (
                        <User size={20} aria-hidden="true" />
                      )}
                    </div>
                    <div className="user-details">
                      <div className="user-name text-oils-primary">
                        {request.fullName}
                      </div>
                      <div className="user-email text-oils-secondary">{request.userEmail}</div>
                    </div>
                  </div>
                </td>
                
                <td className="rank-cell">
                  <span className="rank-badge current-rank" style={getRankStyle(request.currentRank)}>
                    {getRankLabel(request.currentRank)}
                  </span>
                </td>
                
                <td className="rank-cell">
                  <div className="rank-upgrade">
                    <span className="rank-badge requested-rank" style={getRankStyle(request.requestedRank)}>
                      {getRankLabel(request.requestedRank)}
                    </span>
                    <TrendingUp className="upgrade-icon icon-accent-oils" size={16} />
                  </div>
                </td>
                
                <td className="date-cell">
                  <div className="date-info text-oils-secondary">
                    <Calendar size={14} className="icon-oils" />
                    {formatDate(showHistory ? (request.reviewedDate || request.updatedAt || request.requestDate) : request.requestDate)}
                  </div>
                </td>
                
                <td className="status-cell">
                  <span className={`status-badge ${
                    isPendingRequest(request) ? 'badge-pending-oils' :
                    request.status === 'approved' ? 'badge-success-oils' :
                    'badge-error-oils'
                  }`}>
                    <Clock size={14} />
                    {isPendingRequest(request) ? 'În așteptare' :
                     request.status === 'approved' ? 'Aprobat' : 'Respins'}
                  </span>
                </td>
                
                <td className="actions-cell">
                  {isPendingRequest(request) ? (
                    <div className="action-buttons">
                      <button
                        onClick={() => handleApproveRank(request)}
                        disabled={processing[request.id] === 'approving'}
                        className="approve-button btn-primary-oils"
                        title="Aprobă cererea"
                      >
                        {processing[request.id] === 'approving' ? (
                          <RefreshCw size={16} className="spinner-oils" />
                        ) : (
                          <CheckCircle size={16} />
                        )}
                        Aprobă
                      </button>
                      
                      <button
                        onClick={() => {
                          const reason = prompt('Introduceți motivul respingerii (opțional):');
                          if (reason !== null) { // User didn't cancel
                            handleRejectRank(request, reason);
                          }
                        }}
                        disabled={processing[request.id] === 'rejecting'}
                        className="reject-button btn-secondary-oils"
                        title="Respinge cererea"
                      >
                        {processing[request.id] === 'rejecting' ? (
                          <RefreshCw size={16} className="spinner-oils" />
                        ) : (
                          <XCircle size={16} />
                        )}
                        Respinge
                      </button>
                    </div>
                  ) : (
                    <div className="request-processed">
                      {request.status === 'approved' ? (
                        <span className="processed-badge text-success">
                          <CheckCircle size={14} />
                          Procesat
                        </span>
                      ) : (
                        <span className="processed-badge text-error">
                          <XCircle size={14} />
                          Respins
                        </span>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {visibleRequests.length === 0 && (
              <tr>
                <td colSpan="7" className="rank-requests-no-results">Nu am găsit cereri pentru această căutare.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RankRequestsTable;
